import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { BookmarkPlus, Copy, Loader2, Plus, Search, Send, ShoppingCart, Sparkles, Trash2, Undo2, Wand2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { FormField } from '@/components/ui/form-field';
import { NativeSelect } from '@/components/ui/native-select';
import { ernaehrungAPI, ernaehrungVariantenAPI as VA } from '@/lib/api';
import { MAHLZEIT, datum, fehlertext } from '@/pages/ErnaehrungPage';

/* W3.2 Ernährungsplan mit Varianten (29.10.2026, backend/ernaehrung/varianten.py). Ein Plan trägt Varianten (Trainingstag,
   Ruhetag, …) mit eigenem Makroziel und Slots; welche Variante an einem Tag gilt, entscheidet die Steuerung (Coach-Wochenplan,
   Wahl des Kunden, automatisch nach Check-in/Kraftlog). Gerechnet wird NUR auf dem Server: kcal 4/4/9, Mengen je Slot,
   Ist-Summe gegen Ziel (`stand`). Hier wird angezeigt und je Änderung die `version` mitgeschickt — wie im E4-Editor.
   Läuft im CRM (in PlanEditor) und auf /coach (PlanVariantenKarte, über den Spiegel). */

const SLOTS = ['fruehstueck', 'mittag', 'abend', 'snack'];
const TAGE = [['mo', 'Mo'], ['di', 'Di'], ['mi', 'Mi'], ['do', 'Do'], ['fr', 'Fr'], ['sa', 'Sa'], ['so', 'So']];
const TYPEN = { trainingstag: 'Trainingstag', ruhetag: 'Ruhetag', sonstig: 'Sonstiger Tag' };
const PLANARTEN = [['fest', 'Fest', 'Je Mahlzeit ein Gericht'], ['flexibel', 'Flexibel', 'Je Mahlzeit Vorschläge — das Mitglied wählt in der App']];
const STEUERUNGEN = [
  ['coach', 'Coach legt die Woche fest', 'Wochenplan Mo–So → Variante'],
  ['kunde', 'Kunde wählt je Tag', 'Das Mitglied wählt in der App; ohne Wahl gilt der Wochenplan'],
  ['automatisch', 'Automatisch', 'Trainingstag-Variante an Tagen mit Studio-Check-in, Kursbuchung oder Kraftlog-Eintrag, sonst Ruhetag'],
];
const QUELLE = { wochenplan: 'Wochenplan', standard: 'erste Variante', kunde: 'Wahl des Mitglieds', kraftlog: 'Kraftlog', studio_checkin: 'Check-in',
  buchung: 'Kurs gebucht', prognose: 'voraussichtlich', ruhetag: 'kein Training' };
const kcalText = (v) => Number(v || 0).toLocaleString('de-DE');
const zahl = (v) => Number(String(v ?? '').replace(',', '.')) || 0;
const kcalAus = (p, k, f) => Math.round(zahl(p) * 4 + zahl(k) * 4 + zahl(f) * 9);
const vorzeichen = (v) => (v > 0 ? `+${kcalText(v)}` : v < 0 ? `−${kcalText(Math.abs(v))}` : '±0');
const heuteIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

function Wahl({ werte, wert, onWahl, disabled, label }) {
  return (
    <div className="flex flex-wrap gap-1.5" role="group" aria-label={label}>
      {werte.map(([id, text, titel]) => (
        <button key={id} type="button" aria-pressed={wert === id} title={titel} disabled={disabled} onClick={() => wert !== id && onWahl(id)}
          className={`min-h-11 rounded-md border px-3 py-1.5 text-sm text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60 ${wert === id ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-accent'}`}>{text}</button>
      ))}
    </div>
  );
}

/* Ist-Summe gegen Ziel einer Variante (vom Server, `stand`) */
function IstGegenZiel({ stand, quelle }) {
  if (!stand) return null;
  const z = stand.ziel;
  return (
    <div className="rounded-md border px-3 py-2 text-sm space-y-0.5" aria-live="polite">
      <p className="font-medium tabular-nums">
        Ist {kcalText(stand.summe?.kcal)} / Ziel {kcalText(z.kcal)} kcal
        <span className={`ml-2 text-xs ${stand.treffer ? 'text-emerald-700' : 'text-amber-700'}`}>{stand.treffer ? 'innerhalb ± 5 %' : `kcal ${vorzeichen(stand.abweichung.kcal)}`}</span>
      </p>
      <p className="text-xs text-muted-foreground tabular-nums">
        P {kcalText(stand.summe?.eiweiss)}/{kcalText(z.eiweiss)} g · K {kcalText(stand.summe?.kh)}/{kcalText(z.kh)} g · F {kcalText(stand.summe?.fett)}/{kcalText(z.fett)} g
        {stand.summe_pflicht && stand.summe_pflicht.kcal !== stand.summe?.kcal ? ` · nur Pflicht ${kcalText(stand.summe_pflicht.kcal)} kcal` : ''}
        {stand.leer ? ` · ${stand.leer} leer` : ''}
      </p>
      {quelle === 'profil' && <p className="text-xs text-muted-foreground">Ziel aus „Ziele &amp; Zugang“ (Tagesprofil gleichen Typs) — dort ändern.</p>}
    </div>
  );
}

/* Neuer Plan mit Varianten */
export function VariantenNeu({ leadId, onErzeugt, onAbbrechen }) {
  const [f, setF] = useState({ start: heuteIso(), tage: '7', name: '', planart: 'fest', steuerung: 'automatisch', vorlage_id: '', fuellen: true, zuweisen: false });
  const [vorlagen, setVorlagen] = useState([]);
  const [laeuft, setLaeuft] = useState(false);
  useEffect(() => { ernaehrungAPI.vorlagen().then((r) => setVorlagen((r.data.vorlagen || []).filter((v) => v.varianten?.length))).catch(() => {}); }, []);
  const setze = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const erzeugen = async () => {
    setLaeuft(true);
    try {
      const { data } = await VA.neu(leadId, { start: f.start, tage: Number(f.tage), name: f.name.trim() || null, planart: f.planart, steuerung: f.steuerung,
        vorlage_id: f.vorlage_id || null, fuellen: f.fuellen, status: f.zuweisen ? 'zugewiesen' : 'entwurf' });
      (data.fuellen_hinweise || []).forEach((h) => toast.warning(h));
      toast.success(f.zuweisen ? 'Plan mit Varianten angelegt und zugewiesen' : 'Plan mit Varianten als Entwurf angelegt');
      onErzeugt(data);
    } catch (e) { toast.error(fehlertext(e)); } finally { setLaeuft(false); }
  };
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <FormField label="Start"><Input type="date" value={f.start} onChange={(e) => setze('start')(e.target.value)} /></FormField>
        <FormField label="Dauer"><NativeSelect value={f.tage} onChange={setze('tage')} options={[7, 14].map((n) => ({ value: String(n), label: `${n} Tage` }))} /></FormField>
        <FormField label="Name (optional)" className="col-span-2"><Input maxLength={80} value={f.name} placeholder="z. B. Aufbau mit Trainingstagen" onChange={(e) => setze('name')(e.target.value)} /></FormField>
        <FormField label="Grundlage" className="col-span-2"><NativeSelect value={f.vorlage_id} onChange={setze('vorlage_id')}
          options={[{ value: '', label: 'Trainingstag + Ruhetag (aus Zielen und Kundenprofil)' }, ...vorlagen.map((v) => ({ value: v.id, label: `Vorlage: ${v.name} (${v.varianten.length} Varianten)` }))]} /></FormField>
      </div>
      <section className="space-y-1.5"><h4 className="text-sm font-semibold">Planart</h4><Wahl label="Planart" werte={PLANARTEN} wert={f.planart} onWahl={setze('planart')} /></section>
      <section className="space-y-1.5"><h4 className="text-sm font-semibold">Plansteuerung</h4><Wahl label="Plansteuerung" werte={STEUERUNGEN} wert={f.steuerung} onWahl={setze('steuerung')} />
        <p className="text-xs text-muted-foreground">{STEUERUNGEN.find((s) => s[0] === f.steuerung)?.[2]}</p></section>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.fuellen} onChange={(e) => setze('fuellen')(e.target.checked)} />Slots mit passenden Rezepten füllen (Ausschlüsse zuerst)</label>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.zuweisen} onChange={(e) => setze('zuweisen')(e.target.checked)} />Sofort zuweisen (sonst Entwurf)</label>
      <div className="flex gap-2">
        <Button type="button" className="min-h-11 flex-1" disabled={laeuft} onClick={erzeugen}>{laeuft ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}Plan mit Varianten anlegen</Button>
        {onAbbrechen && <Button type="button" variant="ghost" className="min-h-11" onClick={onAbbrechen}>Abbrechen</Button>}
      </div>
    </div>
  );
}

/* Rezepte zum Befüllen: Klick setzt in den gewählten Slot, Ziehen geht auf jeden Slot. Filter: Kochbuch, Mahlzeit, kcal,
   „nur passende“ (Allergene und Ausschlüsse des Plans — rechnet der Server). */
function Rezeptwahl({ leadId, planId, ziel, onWahl }) {
  const [daten, setDaten] = useState(null);
  const [q, setQ] = useState('');
  const [kochbuch, setKochbuch] = useState('');
  const [nurPassend, setNurPassend] = useState(true);
  const [kcalMax, setKcalMax] = useState('');
  const [mahlzeit, setMahlzeit] = useState('');
  useEffect(() => {
    let aktuell = true;
    VA.rezeptwahl(leadId, planId).then((r) => { if (aktuell) setDaten(r.data); }).catch((e) => { if (aktuell) { setDaten({ rezepte: [], kochbuecher: [] }); toast.error(fehlertext(e)); } });
    return () => { aktuell = false; };
  }, [leadId, planId]);
  useEffect(() => { if (ziel?.slot) setMahlzeit(ziel.slot); }, [ziel?.slot]);
  const liste = useMemo(() => {
    if (!daten) return [];
    const kb = daten.kochbuecher.find((k) => k.id === kochbuch);
    const such = q.trim().toLowerCase();
    return daten.rezepte.filter((r) => (!nurPassend || r.passt) && (!kb || kb.rezept_ids.includes(r.id)) && (!mahlzeit || r.mahlzeiten.includes(mahlzeit))
      && (!kcalMax || r.naehrwerte.kcal <= zahl(kcalMax)) && (!such || r.name.toLowerCase().includes(such) || r.tags.some((t) => t.includes(such))));
  }, [daten, q, kochbuch, nurPassend, kcalMax, mahlzeit]);
  if (!daten) return <Skeleton className="h-48 w-full" />;
  return (
    <div className="space-y-2">
      <h4 className="text-sm font-semibold">Rezepte {ziel ? <span className="font-normal text-muted-foreground">→ {ziel.titel}</span> : <span className="font-normal text-muted-foreground">(Slot wählen oder ziehen)</span>}</h4>
      <div className="relative"><Search className="absolute left-2 top-2.5 w-4 h-4 text-muted-foreground" aria-hidden="true" />
        <Input aria-label="Rezept suchen" placeholder="Suchen" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8" /></div>
      <div className="grid grid-cols-2 gap-1.5">
        <NativeSelect aria-label="Kochbuch" value={kochbuch} onChange={setKochbuch} options={[{ value: '', label: 'Alle Kochbücher' }, ...daten.kochbuecher.map((k) => ({ value: k.id, label: k.titel || 'Kochbuch' }))]} className="h-9 py-0" />
        <NativeSelect aria-label="Mahlzeit" value={mahlzeit} onChange={setMahlzeit} options={[{ value: '', label: 'Alle Mahlzeiten' }, ...SLOTS.map((s) => ({ value: s, label: MAHLZEIT[s] }))]} className="h-9 py-0" />
        <Input aria-label="höchstens kcal (Grundportion)" inputMode="numeric" placeholder="max. kcal" value={kcalMax} onChange={(e) => setKcalMax(e.target.value)} className="h-9" />
        <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={nurPassend} onChange={(e) => setNurPassend(e.target.checked)} />nur passende</label>
      </div>
      {daten.ausschluesse?.length > 0 && <p className="text-[11px] text-muted-foreground">Ausschlüsse im Plan: {daten.ausschluesse.join(', ')}</p>}
      <ul className="max-h-[28rem] overflow-y-auto space-y-1 pr-1" aria-label="Rezepte">
        {liste.map((r) => (
          <li key={r.id}>
            <button type="button" draggable onDragStart={(e) => { e.dataTransfer.setData('text/plain', r.id); e.dataTransfer.effectAllowed = 'copy'; }}
              disabled={!ziel || !r.passt} onClick={() => onWahl(r.id)} title={r.passt ? 'Klicken setzt in den gewählten Slot, Ziehen auf einen Slot' : 'Passt nicht zu den Ausschlüssen des Plans'}
              className="w-full min-h-11 rounded-md border px-2 py-1.5 text-left text-sm hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-grab disabled:opacity-70">
              <span className="block font-medium leading-snug">{r.name}{r.eigenes ? ' · eigenes' : ''}</span>
              <span className="block text-[11px] text-muted-foreground tabular-nums">{r.naehrwerte.kcal} kcal · P {Math.round(r.naehrwerte.eiweiss)} g{r.mahlzeiten.length ? ` · ${r.mahlzeiten.map((m) => MAHLZEIT[m]).join(', ')}` : ''}
                {!r.passt ? ' · passt nicht' : ''}{r.allergene.length ? ` · ${r.allergene.join(', ')}` : ''}</span>
            </button>
          </li>
        ))}
        {!liste.length && <li className="text-xs text-muted-foreground">Kein Rezept passt zu den Filtern.</li>}
      </ul>
    </div>
  );
}

function Gericht({ m, stand, onWeg, weg }) {
  return (
    <div className="flex items-start justify-between gap-2 rounded-md bg-muted/40 px-2 py-1.5">
      <div className="min-w-0">
        <p className="text-sm font-medium leading-snug break-words">{m.name}</p>
        <p className={`text-[11px] tabular-nums ${stand && !stand.treffer ? 'text-amber-700' : 'text-muted-foreground'}`}>
          {m.naehrwerte.kcal} kcal · P {Math.round(m.naehrwerte.eiweiss)} · K {Math.round(m.naehrwerte.kh)} · F {Math.round(m.naehrwerte.fett)} g
          {stand ? ` · Ziel ${kcalText(stand.ziel.kcal)} (${vorzeichen(stand.abweichung.kcal)})` : ''}</p>
      </div>
      {onWeg && <button type="button" aria-label={weg} title={weg} onClick={onWeg} className="shrink-0 rounded p-2 text-muted-foreground hover:text-destructive focus-visible:ring-2 focus-visible:ring-ring"><X className="w-4 h-4" /></button>}
    </div>
  );
}

function SlotKarte({ s, flexibel, aktiv, onZiel, onDrop, onAktion, laeuft }) {
  const [drueber, setDrueber] = useState(false);
  const gerichte = flexibel ? (s.vorschlaege || []) : (s.mahlzeit ? [{ rezept_id: s.rezept_id, mahlzeit: s.mahlzeit, stand: s.stand }] : []);
  return (
    <div onDragOver={(e) => { e.preventDefault(); setDrueber(true); }} onDragLeave={() => setDrueber(false)}
      onDrop={(e) => { e.preventDefault(); setDrueber(false); const rid = e.dataTransfer.getData('text/plain'); if (rid) onDrop(rid); }}
      className={`rounded-lg border p-3 space-y-2 transition-colors ${aktiv ? 'border-primary ring-1 ring-primary' : ''} ${drueber ? 'bg-primary/5 border-primary' : ''}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">{s.uhrzeit} · {MAHLZEIT[s.slot]} · {s.anteil} %</p>
          <h5 className="font-semibold leading-snug">{s.titel}{!s.pflicht && <Badge variant="outline" className="ml-2 font-normal">optional</Badge>}</h5>
          <p className="text-[11px] text-muted-foreground tabular-nums">Ziel {kcalText(Math.round(s.ziel?.kcal))} kcal · P {Math.round(s.ziel?.eiweiss || 0)} g</p>
        </div>
        <Button type="button" size="sm" variant={aktiv ? 'secondary' : 'outline'} className="min-h-11" aria-pressed={aktiv} onClick={onZiel}>{aktiv ? 'Ausgewählt' : 'Befüllen'}</Button>
      </div>
      {gerichte.length ? gerichte.map((g) => (
        <Gericht key={g.rezept_id} m={g.mahlzeit} stand={g.stand} weg={flexibel ? 'Vorschlag entfernen' : 'Gericht entfernen'}
          onWeg={laeuft ? null : () => onAktion(flexibel ? 'vorschlag_weg' : 'leeren', g.rezept_id)} />
      )) : <p className="rounded-md border border-dashed px-2 py-3 text-center text-xs text-muted-foreground">Leer — Rezept hierher ziehen oder „Befüllen“</p>}
      {flexibel && gerichte.length > 0 && <p className="text-[11px] text-muted-foreground">{gerichte.length} Vorschläge — das Mitglied wählt in der App, der erste gilt ohne Wahl.</p>}
    </div>
  );
}

/* Titel, Typ, Makroziel (kcal 4/4/9) und Slots einer Variante */
function VarianteKopf({ v, laeuft, onSpeichern }) {
  const neuStand = useCallback(() => ({ titel: v.titel, typ: v.typ, p: String(v.ziele.eiweiss), k: String(v.ziele.kh), f: String(v.ziele.fett),
    slots: Object.fromEntries(SLOTS.map((s) => { const x = v.slots.find((y) => y.slot === s); return [s, x ? { an: true, titel: x.titel, pflicht: x.pflicht, uhrzeit: x.uhrzeit, anteil: String(x.anteil) } : { an: false, titel: MAHLZEIT[s], pflicht: true, uhrzeit: '', anteil: '' }]; })) }), [v]);
  const [f, setF] = useState(neuStand);
  useEffect(() => { setF(neuStand()); }, [neuStand]);
  const an = SLOTS.filter((s) => f.slots[s].an);
  const summe = an.reduce((a, s) => a + zahl(f.slots[s].anteil), 0);
  const setzeSlot = (s, patch) => setF((x) => ({ ...x, slots: { ...x.slots, [s]: { ...x.slots[s], ...patch } } }));
  const speichern = () => {
    const alleAnteile = an.length >= 2 && an.every((s) => String(f.slots[s].anteil).trim() !== '');
    onSpeichern({ titel: f.titel, typ: f.typ, ziele: { eiweiss: zahl(f.p), kh: zahl(f.k), fett: zahl(f.f) },
      slots: an.map((s) => ({ slot: s, titel: f.slots[s].titel, pflicht: f.slots[s].pflicht, uhrzeit: f.slots[s].uhrzeit || null, anteil: alleAnteile ? zahl(f.slots[s].anteil) : null })) });
  };
  return (
    <details className="rounded-lg border px-3 py-2" open={false}>
      <summary className="cursor-pointer list-none text-sm font-medium min-h-11 flex items-center">Variante bearbeiten · {TYPEN[v.typ]} · {kcalText(v.ziele.kcal)} kcal (P {v.ziele.eiweiss} / K {v.ziele.kh} / F {v.ziele.fett} g)</summary>
      <div className="mt-2 space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <FormField label="Titel"><Input maxLength={60} value={f.titel} onChange={(e) => setF((x) => ({ ...x, titel: e.target.value }))} /></FormField>
          <FormField label="Typ"><NativeSelect value={f.typ} onChange={(t) => setF((x) => ({ ...x, typ: t }))} options={Object.entries(TYPEN).map(([value, label]) => ({ value, label }))} /></FormField>
        </div>
        <div className="grid grid-cols-4 gap-2 items-end">
          {[['p', 'Eiweiß g'], ['k', 'Kohlenh. g'], ['f', 'Fett g']].map(([k, l]) => (
            <FormField key={k} label={l}><Input inputMode="numeric" value={f[k]} onChange={(e) => setF((x) => ({ ...x, [k]: e.target.value }))} /></FormField>
          ))}
          <p className="text-sm tabular-nums pb-2" aria-live="polite">= {kcalText(kcalAus(f.p, f.k, f.f))} kcal</p>
        </div>
        <div className="space-y-1.5">
          <h5 className="text-sm font-semibold">Mahlzeiten</h5>
          {SLOTS.map((s) => (
            <div key={s} className="grid grid-cols-[6.5rem_minmax(0,1fr)_4rem_4rem_auto] items-center gap-1.5">
              <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" checked={f.slots[s].an} disabled={f.slots[s].an && an.length <= 1} onChange={(e) => setzeSlot(s, { an: e.target.checked })} />{MAHLZEIT[s]}</label>
              <Input aria-label={`Name ${MAHLZEIT[s]}`} maxLength={40} disabled={!f.slots[s].an} value={f.slots[s].titel} onChange={(e) => setzeSlot(s, { titel: e.target.value })} className="h-9" />
              <Input aria-label={`Uhrzeit ${MAHLZEIT[s]}`} placeholder="HH:MM" disabled={!f.slots[s].an} value={f.slots[s].uhrzeit} onChange={(e) => setzeSlot(s, { uhrzeit: e.target.value })} className="h-9 px-1.5" />
              <Input aria-label={`Anteil ${MAHLZEIT[s]} in Prozent`} placeholder="%" inputMode="numeric" disabled={!f.slots[s].an} value={f.slots[s].anteil} onChange={(e) => setzeSlot(s, { anteil: e.target.value })} className="h-9 px-1.5 text-right" />
              <label className="flex items-center gap-1 text-xs"><input type="checkbox" disabled={!f.slots[s].an} checked={f.slots[s].pflicht} onChange={(e) => setzeSlot(s, { pflicht: e.target.checked })} />Pflicht</label>
            </div>
          ))}
          <p className={`text-xs ${summe === 100 || an.length < 2 ? 'text-muted-foreground' : 'text-amber-700'}`}>Anteile {summe} % — leer lassen verteilt nach dem Standard.</p>
        </div>
        <Button type="button" className="min-h-11" disabled={laeuft} onClick={speichern}>Variante speichern</Button>
      </div>
    </details>
  );
}

/* Wochenplan Mo–So → Variante, darunter die Tage des Plans, wie sie gelten (vom Server, mit Quelle) */
function Wochenraster({ plan, laeuft, onSpeichern }) {
  const [wp, setWp] = useState(plan.wochenplan || {});
  useEffect(() => { setWp(plan.wochenplan || {}); }, [plan.wochenplan]);
  const geaendert = JSON.stringify(wp) !== JSON.stringify(plan.wochenplan || {});
  const optionen = [{ value: '', label: '–' }, ...plan.varianten.map((v) => ({ value: v.id, label: v.titel }))];
  return (
    <section className="space-y-2">
      <h4 className="text-sm font-semibold">{plan.steuerung === 'automatisch' ? 'Wochenplan (Prognose für kommende Tage ohne Buchung)' : 'Wochenplan'}</h4>
      <div className="grid grid-cols-7 gap-1">
        {TAGE.map(([t, l]) => (
          <FormField key={t} label={l}><NativeSelect aria-label={`Variante ${l}`} value={wp[t] || ''} onChange={(v) => setWp((x) => ({ ...x, [t]: v || null }))} options={optionen} className="h-11 px-1 py-0 text-xs" /></FormField>
        ))}
      </div>
      {geaendert && <Button type="button" size="sm" className="min-h-11" disabled={laeuft} onClick={() => onSpeichern(Object.fromEntries(Object.entries(wp).filter(([, v]) => v)))}>Wochenplan speichern</Button>}
      <ul className="flex gap-1 overflow-x-auto text-xs" aria-label="Variante je Tag">
        {plan.tage.map((t) => (
          <li key={t.datum} className="shrink-0 rounded border px-2 py-1" title={t.variante?.hinweis || ''}>
            <span className="block tabular-nums text-muted-foreground">{datum(t.datum, { weekday: 'short', day: '2-digit', month: '2-digit' })}</span>
            <span className="block font-medium">{t.variante?.titel}</span>
            <span className="block text-muted-foreground">{QUELLE[t.variante?.quelle] || t.variante?.quelle}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function EinkaufWoche({ leadId, start, offenStart }) {
  const [offen, setOffen] = useState(!!offenStart);
  const [liste, setListe] = useState(null);
  useEffect(() => {
    if (!offen) return;
    VA.einkaufWoche(leadId, start).then((r) => setListe(r.data)).catch((e) => toast.error(fehlertext(e)));
  }, [offen, leadId, start]);
  return (
    <details className="rounded-lg border px-3 py-2" open={!!offenStart} onToggle={(e) => setOffen(e.currentTarget.open)}>
      <summary className="cursor-pointer list-none text-sm font-medium min-h-11 flex items-center gap-1.5"><ShoppingCart className="w-4 h-4" aria-hidden="true" />Einkaufsliste der Woche (so sieht sie das Mitglied)</summary>
      {!liste ? <Skeleton className="h-16 w-full mt-2" /> : (
        <div className="mt-2 space-y-2 text-sm">
          <p className="text-xs text-muted-foreground">{datum(liste.von)} – {datum(liste.bis)} · {liste.tage.length} Tage · alle Varianten-Tage zusammengezählt</p>
          {liste.abteilungen.map((a) => (
            <div key={a.id}><h5 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{a.name}</h5>
              <ul>{a.eintraege.map((e) => <li key={e.lebensmittel_id} className={`flex justify-between gap-2 ${e.abgehakt ? 'line-through text-muted-foreground' : ''}`}><span>{e.name}</span><span className="tabular-nums">{kcalText(Math.round(e.gramm))} g</span></li>)}</ul></div>
          ))}
          {!liste.eintraege.length && <p className="text-xs text-muted-foreground">Für diese Woche steht nichts auf der Liste.</p>}
        </div>
      )}
    </details>
  );
}


/* ---- Aufbau wie im Mockup „Ernährungsplan-Editor“ (nur /coach, stil="coach") ---- */
const KARTE = 'rounded-[14px] border border-border bg-card px-5 py-[18px]';
const CHIP = 'inline-flex min-h-11 items-center gap-1.5 rounded-full border px-[11px] py-[5px] text-xs font-bold focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60';
const chipKlasse = (an) => `${CHIP} ${an ? 'border-marke bg-marke text-[#140c08]' : 'border-border bg-muted text-foreground hover:bg-accent'}`;
const KNOPF = 'inline-flex min-h-11 items-center justify-center gap-1.5 rounded-[10px] border border-border bg-muted px-4 text-sm font-bold text-foreground hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60';

function Segment({ werte, wert, onWahl, disabled, label }) {
  return (
    <div className="inline-flex flex-wrap overflow-hidden rounded-[10px] border border-border" role="group" aria-label={label}>
      {werte.map(([id, text, titel]) => (
        <button key={id} type="button" aria-pressed={wert === id} title={titel} disabled={disabled} onClick={() => wert !== id && onWahl(id)}
          className={`min-h-11 px-3.5 text-[13px] font-bold focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60 ${wert === id ? 'bg-marke text-[#140c08]' : 'bg-card text-muted-foreground hover:bg-accent'}`}>{text}</button>
      ))}
    </div>
  );
}

function MakroKachel({ name, ist, ziel, einheit }) {
  return (
    <div className="rounded-[10px] border border-border bg-background px-3 py-2.5">
      <div className="text-[13px] text-muted-foreground">{name}</div>
      <div className="coach-zahl text-lg font-extrabold tabular-nums">{ist == null ? '–' : kcalText(Math.round(ist))}{einheit}</div>
      <div className="text-[13px] text-muted-foreground tabular-nums">Ziel {kcalText(Math.round(ziel || 0))}</div>
    </div>
  );
}

function SlotZeile({ s, flexibel, aktiv, onZiel, onDrop, onAktion, laeuft }) {
  const [drueber, setDrueber] = useState(false);
  const gerichte = flexibel ? (s.vorschlaege || []) : (s.mahlzeit ? [{ rezept_id: s.rezept_id, mahlzeit: s.mahlzeit, stand: s.stand }] : []);
  return (
    <div data-testid={`slot-${s.slot}`} onDragOver={(e) => { e.preventDefault(); setDrueber(true); }} onDragLeave={() => setDrueber(false)}
      onDrop={(e) => { e.preventDefault(); setDrueber(false); const rid = e.dataTransfer.getData('text/plain'); if (rid) onDrop(rid); }}
      className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-background px-3.5 py-3 transition-colors ${aktiv || drueber ? 'border-marke ring-1 ring-marke' : 'border-border'}`}>
      <div className="min-w-0 flex-1 basis-56">
        <div className="text-[13px] text-muted-foreground">{s.uhrzeit ? `${s.uhrzeit} · ` : ''}{s.titel}{s.pflicht ? ' · Pflicht' : ''} · Ziel {kcalText(Math.round(s.ziel?.kcal))} kcal</div>
        {gerichte.length ? gerichte.map((g) => (
          <div key={g.rezept_id} className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <strong className="break-words">{g.mahlzeit.name}</strong>
              <div className="text-[13px] text-muted-foreground tabular-nums">{g.mahlzeit.naehrwerte.kcal} kcal · P {Math.round(g.mahlzeit.naehrwerte.eiweiss)} · K {Math.round(g.mahlzeit.naehrwerte.kh)} · F {Math.round(g.mahlzeit.naehrwerte.fett)} g</div>
            </div>
            {!laeuft && <button type="button" aria-label={flexibel ? 'Vorschlag entfernen' : 'Gericht entfernen'} title={flexibel ? 'Vorschlag entfernen' : 'Gericht entfernen'}
              onClick={() => onAktion(flexibel ? 'vorschlag_weg' : 'leeren', g.rezept_id)} className="grid h-11 w-11 shrink-0 place-items-center rounded text-muted-foreground hover:text-destructive focus-visible:ring-2 focus-visible:ring-ring"><X className="h-4 w-4" /></button>}
          </div>
        )) : <div className="text-[13px] text-muted-foreground">Leer — Rezept hierher ziehen oder „Tauschen“</div>}
        {flexibel && gerichte.length > 0 && <div className="text-[13px] text-muted-foreground">{gerichte.length} Vorschläge — das Mitglied wählt in der App, der erste gilt ohne Wahl.</div>}
      </div>
      <button type="button" aria-pressed={aktiv} onClick={onZiel} className={KNOPF}>{aktiv ? 'Ausgewählt' : gerichte.length ? 'Tauschen' : 'Befüllen'}</button>
    </div>
  );
}

function CoachWochenraster({ plan, laeuft, onSpeichern }) {
  const [wp, setWp] = useState(plan.wochenplan || {});
  useEffect(() => { setWp(plan.wochenplan || {}); }, [plan.wochenplan]);
  const geaendert = JSON.stringify(wp) !== JSON.stringify(plan.wochenplan || {});
  const optionen = [{ value: '', label: '–' }, ...plan.varianten.map((v) => ({ value: v.id, label: v.titel }))];
  return (
    <div className="space-y-2">
      <div className="text-[13px] text-muted-foreground">{plan.steuerung === 'automatisch' ? 'Wochenplan (Prognose für kommende Tage ohne Buchung)' : 'Wochenplan Mo–So → Variante'}</div>
      <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-7">
        {TAGE.map(([t, l]) => (
          <div key={t} className="flex flex-col items-center gap-1.5 text-xs">
            <span className="text-muted-foreground">{l}</span>
            <NativeSelect aria-label={`Variante ${l}`} value={wp[t] || ''} onChange={(v) => setWp((x) => ({ ...x, [t]: v || null }))} options={optionen} className="h-11 w-full px-1 py-0 text-xs" />
          </div>
        ))}
      </div>
      {geaendert && <button type="button" className={`${KNOPF} border-primary bg-primary text-primary-foreground hover:bg-primary/90`} disabled={laeuft}
        onClick={() => onSpeichern(Object.fromEntries(Object.entries(wp).filter(([, v]) => v)))}>Wochenplan speichern</button>}
      <ul className="flex gap-1.5 overflow-x-auto text-xs" aria-label="Variante je Tag">
        {plan.tage.map((t) => (
          <li key={t.datum} className="shrink-0 rounded-lg border border-border px-2 py-1" title={t.variante?.hinweis || ''}>
            <span className="block tabular-nums text-muted-foreground">{datum(t.datum, { weekday: 'short', day: '2-digit', month: '2-digit' })}</span>
            <span className="block font-bold">{t.variante?.titel}</span>
            <span className="block text-muted-foreground">{QUELLE[t.variante?.quelle] || t.variante?.quelle}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CoachVariante({ leadId, plan, v, flexibel, laeuft, run, setAktivId, ziel, setZiel, slotAktion, befuellen, neueVariante, duplizieren, loeschen, alsVorlage, fuss }) {
  const st = v.stand;
  const z = st?.ziel || v.ziele;
  return (
    <div className="space-y-[18px]" data-testid="varianten-editor">
      <section className={`${KARTE} flex flex-wrap items-center gap-5`}>
        <div>
          <div className="mb-1.5 text-[13px] text-muted-foreground">Planart</div>
          <Segment label="Planart" werte={[['fest', 'Fest: eine Mahlzeit je Slot', PLANARTEN[0][2]], ['flexibel', 'Flexibel: Vorschläge', PLANARTEN[1][2]]]} wert={plan.planart} disabled={laeuft}
            onWahl={(p) => run(() => VA.steuerung(leadId, plan.id, { version: plan.version, planart: p }), p === 'flexibel' ? 'Flexibel — das Mitglied wählt je Mahlzeit' : 'Fest — je Mahlzeit ein Gericht')} />
        </div>
        <div>
          <div className="mb-1.5 text-[13px] text-muted-foreground">Planvarianten</div>
          <div role="tablist" aria-label="Varianten" className="flex flex-wrap gap-1.5">
            {plan.varianten.map((x) => (
              <button key={x.id} type="button" role="tab" aria-selected={x.id === v.id} onClick={() => { setAktivId(x.id); setZiel(null); }} className={chipKlasse(x.id === v.id)}>
                {x.titel} · {kcalText(x.ziel_wirksam?.kcal ?? x.ziele.kcal)} kcal{x.stand && !x.stand.treffer ? ' · weicht ab' : ''}
              </button>
            ))}
            <button type="button" className={chipKlasse(false)} disabled={laeuft} onClick={neueVariante}><Plus className="h-3.5 w-3.5" aria-hidden="true" />Variante</button>
          </div>
        </div>
      </section>
      {plan.hinweise?.length > 0 && <ul className="coach-hinweis rounded-[10px] px-3 py-2 text-xs coach-hinweis-text" role="status">{plan.hinweise.map((h) => <li key={h}>{h}</li>)}</ul>}

      <div className="flex flex-wrap items-start gap-[18px]">
        <section className={`${KARTE} flex min-w-0 flex-[2_1_520px] flex-col gap-3`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="m-0 text-base font-bold">Variante „{v.titel}“</h2>
            <div className="flex flex-wrap gap-1.5">
              <button type="button" className={KNOPF} disabled={laeuft} onClick={() => run(() => VA.fuellen(leadId, plan.id, { version: plan.version, variante_id: v.id }), 'Leere Slots gefüllt')}><Wand2 className="h-4 w-4" aria-hidden="true" />Leere füllen</button>
              <button type="button" className={KNOPF} disabled={laeuft} onClick={duplizieren}><Copy className="h-4 w-4" aria-hidden="true" />Duplizieren</button>
              <button type="button" className={KNOPF} onClick={() => alsVorlage(true)}><BookmarkPlus className="h-4 w-4" aria-hidden="true" />Variante als Vorlage</button>
              <button type="button" className={`${KNOPF} text-destructive`} disabled={laeuft || plan.varianten.length <= 1} onClick={loeschen}><Trash2 className="h-4 w-4" aria-hidden="true" />Löschen</button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <MakroKachel name="Kalorien" ist={st?.summe?.kcal} ziel={z.kcal} einheit="" />
            <MakroKachel name="Protein" ist={st?.summe?.eiweiss} ziel={z.eiweiss} einheit=" g" />
            <MakroKachel name="Kohlenhydrate" ist={st?.summe?.kh} ziel={z.kh} einheit=" g" />
            <MakroKachel name="Fett" ist={st?.summe?.fett} ziel={z.fett} einheit=" g" />
          </div>
          {st && <p className={`text-[13px] tabular-nums ${st.treffer ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-300'}`} aria-live="polite">
            {st.treffer ? 'Innerhalb ± 5 % des Ziels' : `Kalorien ${vorzeichen(st.abweichung.kcal)} gegen Ziel`}
            {st.summe_pflicht && st.summe_pflicht.kcal !== st.summe?.kcal ? ` · nur Pflicht ${kcalText(st.summe_pflicht.kcal)} kcal` : ''}{st.leer ? ` · ${st.leer} leer` : ''}</p>}
          {v.ziel_quelle === 'profil' && <p className="text-[13px] text-muted-foreground">Ziel aus „Ziele &amp; Zugang“ (Tagesprofil gleichen Typs) — dort ändern.</p>}
          <VarianteKopf v={v} laeuft={laeuft} onSpeichern={(daten) => run(() => VA.varianteAendern(leadId, plan.id, v.id, { version: plan.version, ...daten }), 'Variante gespeichert')} />
          <div className="text-[13px] text-muted-foreground">Mahlzeitslots · Namen frei wählbar</div>
          {v.slots.map((s) => (
            <SlotZeile key={s.slot} s={s} flexibel={flexibel} laeuft={laeuft} aktiv={ziel?.vid === v.id && ziel?.slot === s.slot}
              onZiel={() => setZiel(ziel?.vid === v.id && ziel?.slot === s.slot ? null : { vid: v.id, slot: s.slot, titel: s.titel })}
              onDrop={(rid) => befuellen(v.id, s.slot, rid)} onAktion={(aktion, rid) => slotAktion(v.id, s.slot, aktion, rid)} />
          ))}
          <div className="rounded-xl border-[1.5px] border-dashed border-border p-3.5 text-center text-[13px] text-muted-foreground">Rezept hierher ziehen oder bei einem Slot „Tauschen“ wählen und rechts ein Rezept anklicken</div>

          <h2 className="m-0 mt-2 text-base font-bold">Plansteuerung</h2>
          <Segment label="Plansteuerung" werte={STEUERUNGEN} wert={plan.steuerung} disabled={laeuft}
            onWahl={(s) => run(() => VA.steuerung(leadId, plan.id, { version: plan.version, steuerung: s }), 'Steuerung geändert')} />
          <div className="text-[13px] text-muted-foreground">{STEUERUNGEN.find((s) => s[0] === plan.steuerung)?.[2]}</div>
          <CoachWochenraster plan={plan} laeuft={laeuft} onSpeichern={(wp) => run(() => VA.steuerung(leadId, plan.id, { version: plan.version, wochenplan: wp }), 'Wochenplan gespeichert')} />
          <div className="flex flex-wrap gap-2">
            {fuss}
            <button type="button" className={KNOPF} onClick={() => alsVorlage(false)}><BookmarkPlus className="h-4 w-4" aria-hidden="true" />Als Vorlage speichern</button>
          </div>
        </section>

        <div className="flex min-w-0 flex-[1_1_300px] flex-col gap-[18px]">
          <aside className={KARTE}>
            <h2 className="m-0 mb-3 text-base font-bold">Rezepte aus Kochbüchern</h2>
            <Rezeptwahl leadId={leadId} planId={plan.id} ziel={ziel?.vid === v.id ? ziel : null} onWahl={(rid) => ziel && befuellen(ziel.vid, ziel.slot, rid)} />
            <div className="mt-2 text-[13px] text-muted-foreground">Klick wählt den Slot, Ziehen geht auch.</div>
          </aside>
          <section className={KARTE}>
            <h2 className="m-0 mb-1 text-base font-bold">Einkaufsliste · Woche</h2>
            <EinkaufWoche leadId={leadId} start={plan.start} offenStart />
          </section>
        </div>
      </div>
    </div>
  );
}

/* Editor eines geladenen Varianten-Plans. `onPlan` bekommt den neuen Plan (Antwort des Servers). */
export function VariantenEditor({ leadId, plan, onPlan, stil, fuss }) {
  const [aktivId, setAktivId] = useState(plan.varianten[0]?.id);
  const [ziel, setZiel] = useState(null);           // {vid, slot, titel}
  const [laeuft, setLaeuft] = useState(false);
  useEffect(() => { if (!plan.varianten.some((v) => v.id === aktivId)) setAktivId(plan.varianten[0]?.id); }, [plan.varianten, aktivId]);
  const v = plan.varianten.find((x) => x.id === aktivId) || plan.varianten[0];
  const flexibel = plan.planart === 'flexibel';
  const run = async (fn, erfolg) => {
    setLaeuft(true);
    try {
      const { data } = await fn();
      (data.fuellen_hinweise || []).forEach((h) => toast.warning(h));
      onPlan(data);
      if (erfolg) toast.success(erfolg);
      return data;
    } catch (e) {
      toast.error(fehlertext(e));
      if (e?.response?.status === 409) { try { onPlan((await ernaehrungAPI.planLesen(leadId, plan.id)).data); } catch { /* bleibt */ } }
      return null;
    } finally { setLaeuft(false); }
  };
  const slotAktion = (vid, slot, aktion, rezeptId) => run(() => VA.slot(leadId, plan.id, vid, slot, { version: plan.version, aktion, rezept_id: rezeptId || null }));
  const befuellen = (vid, slot, rezeptId) => slotAktion(vid, slot, flexibel ? 'vorschlag_dazu' : 'setzen', rezeptId);
  const neueVariante = async () => {
    const titel = window.prompt('Name der neuen Variante (z. B. Refeed, Wettkampftag):', 'Neue Variante');
    if (!titel || titel.trim().length < 1) return;
    const data = await run(() => VA.varianteNeu(leadId, plan.id, { version: plan.version, titel: titel.trim(), typ: 'sonstig' }), 'Variante angelegt');
    if (data) setAktivId(data.varianten[data.varianten.length - 1].id);
  };
  const duplizieren = async () => {
    const data = await run(() => VA.varianteDuplizieren(leadId, plan.id, v.id, plan.version), 'Variante dupliziert');
    if (data) setAktivId(data.varianten[data.varianten.length - 1].id);
  };
  const loeschen = () => { if (window.confirm(`Variante „${v.titel}“ löschen?`)) run(() => VA.varianteLoeschen(leadId, plan.id, v.id, plan.version), 'Variante gelöscht'); };
  const alsVorlage = async (nurDiese) => {
    const name = window.prompt(nurDiese ? `Name der Vorlage (nur „${v.titel}“):` : 'Name der Vorlage (alle Varianten, Ziele und Rezepte, keine Mengen):', plan.name || v.titel);
    if (!name || name.trim().length < 2) return;
    try { const { data } = await VA.alsVorlage(leadId, plan.id, { name: name.trim(), variante_id: nurDiese ? v.id : null }); toast.success(`Vorlage „${data.name}“ angelegt`); }
    catch (e) { toast.error(fehlertext(e)); }
  };
  if (!v) return null;
  if (stil === 'coach') {
    return <CoachVariante leadId={leadId} plan={plan} v={v} flexibel={flexibel} laeuft={laeuft} run={run} setAktivId={setAktivId} ziel={ziel} setZiel={setZiel}
      slotAktion={slotAktion} befuellen={befuellen} neueVariante={neueVariante} duplizieren={duplizieren} loeschen={loeschen} alsVorlage={alsVorlage} fuss={fuss} />;
  }
  return (
    <div className="space-y-4" data-testid="varianten-editor">
      <div className="grid gap-3 md:grid-cols-2">
        <section className="space-y-1.5"><h4 className="text-sm font-semibold">Planart</h4>
          <Wahl label="Planart" werte={PLANARTEN} wert={plan.planart} disabled={laeuft} onWahl={(p) => run(() => VA.steuerung(leadId, plan.id, { version: plan.version, planart: p }), p === 'flexibel' ? 'Flexibel — das Mitglied wählt je Mahlzeit' : 'Fest — je Mahlzeit ein Gericht')} /></section>
        <section className="space-y-1.5"><h4 className="text-sm font-semibold">Plansteuerung</h4>
          <Wahl label="Plansteuerung" werte={STEUERUNGEN} wert={plan.steuerung} disabled={laeuft} onWahl={(s) => run(() => VA.steuerung(leadId, plan.id, { version: plan.version, steuerung: s }), 'Steuerung geändert')} />
          <p className="text-xs text-muted-foreground">{STEUERUNGEN.find((s) => s[0] === plan.steuerung)?.[2]}</p></section>
      </div>
      {plan.hinweise?.length > 0 && <ul className="text-xs text-amber-700 space-y-0.5" role="status">{plan.hinweise.map((h) => <li key={h}>{h}</li>)}</ul>}
      <Wochenraster plan={plan} laeuft={laeuft} onSpeichern={(wp) => run(() => VA.steuerung(leadId, plan.id, { version: plan.version, wochenplan: wp }), 'Wochenplan gespeichert')} />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div role="tablist" aria-label="Varianten" className="flex gap-1.5 overflow-x-auto py-1">
          {plan.varianten.map((x) => (
            <button key={x.id} type="button" role="tab" aria-selected={x.id === v.id} onClick={() => { setAktivId(x.id); setZiel(null); }}
              className={`shrink-0 min-h-11 rounded-md border px-3 py-1.5 text-left text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${x.id === v.id ? 'border-primary bg-primary/5' : 'hover:bg-accent'}`}>
              <span className="block font-medium">{x.titel}</span>
              <span className="block text-[11px] text-muted-foreground tabular-nums">{TYPEN[x.typ]} · {kcalText(x.ziel_wirksam?.kcal ?? x.ziele.kcal)} kcal{x.stand && !x.stand.treffer ? ' · weicht ab' : ''}</span>
            </button>
          ))}
          <Button type="button" size="sm" variant="outline" className="min-h-11 shrink-0" disabled={laeuft} onClick={neueVariante}><Plus className="w-4 h-4 mr-1" />Variante</Button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Button type="button" size="sm" variant="ghost" className="min-h-11" disabled={laeuft} onClick={() => run(() => VA.fuellen(leadId, plan.id, { version: plan.version, variante_id: v.id }), 'Leere Slots gefüllt')}><Wand2 className="w-4 h-4 mr-1" />Leere füllen</Button>
          <Button type="button" size="sm" variant="ghost" className="min-h-11" disabled={laeuft} onClick={duplizieren}><Copy className="w-4 h-4 mr-1" />Duplizieren</Button>
          <Button type="button" size="sm" variant="ghost" className="min-h-11" onClick={() => alsVorlage(true)}><BookmarkPlus className="w-4 h-4 mr-1" />Variante als Vorlage</Button>
          <Button type="button" size="sm" variant="ghost" className="min-h-11" onClick={() => alsVorlage(false)}><BookmarkPlus className="w-4 h-4 mr-1" />Plan als Vorlage</Button>
          <Button type="button" size="sm" variant="ghost" className="min-h-11 text-destructive" disabled={laeuft || plan.varianten.length <= 1} onClick={loeschen}><Trash2 className="w-4 h-4 mr-1" />Löschen</Button>
        </div>
      </div>

      <div className="grid gap-4 items-start lg:grid-cols-[minmax(0,1fr)_18rem]">
        <div className="min-w-0 space-y-3">
          <IstGegenZiel stand={v.stand} quelle={v.ziel_quelle} />
          <VarianteKopf v={v} laeuft={laeuft} onSpeichern={(daten) => run(() => VA.varianteAendern(leadId, plan.id, v.id, { version: plan.version, ...daten }), 'Variante gespeichert')} />
          <div className="grid gap-3 md:grid-cols-2">
            {v.slots.map((s) => (
              <SlotKarte key={s.slot} s={s} flexibel={flexibel} laeuft={laeuft} aktiv={ziel?.vid === v.id && ziel?.slot === s.slot}
                onZiel={() => setZiel(ziel?.vid === v.id && ziel?.slot === s.slot ? null : { vid: v.id, slot: s.slot, titel: s.titel })}
                onDrop={(rid) => befuellen(v.id, s.slot, rid)} onAktion={(aktion, rid) => slotAktion(v.id, s.slot, aktion, rid)} />
            ))}
          </div>
        </div>
        <div className="rounded-lg border p-3 lg:sticky lg:top-2">
          <Rezeptwahl leadId={leadId} planId={plan.id} ziel={ziel?.vid === v.id ? ziel : null} onWahl={(rid) => ziel && befuellen(ziel.vid, ziel.slot, rid)} />
        </div>
      </div>
      <EinkaufWoche leadId={leadId} start={plan.start} />
    </div>
  );
}

/* Eigenständige Karte (Trainer-Webseite /coach, Reiter Plan): Varianten-Pläne des Mitglieds wählen, anlegen, zuweisen */
export default function PlanVariantenKarte({ leadId }) {
  const [liste, setListe] = useState(null);
  const [gewaehlt, setGewaehlt] = useState(null);
  const [plan, setPlan] = useState(null);
  const [neu, setNeu] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const ladenListe = useCallback(async () => {
    try { setListe(((await ernaehrungAPI.plaene(leadId)).data.plaene || []).filter((p) => p.varianten)); } catch (e) { setListe([]); toast.error(fehlertext(e, 'Pläne konnten nicht geladen werden')); }
  }, [leadId]);
  useEffect(() => { ladenListe(); }, [ladenListe]);
  useEffect(() => {
    if (!gewaehlt) { setPlan(null); return; }
    ernaehrungAPI.planLesen(leadId, gewaehlt).then((r) => setPlan(r.data)).catch((e) => toast.error(fehlertext(e)));
  }, [leadId, gewaehlt]);
  const status = async (s) => {
    setLaeuft(true);
    try { const { data } = await ernaehrungAPI.planStatus(leadId, plan.id, s); setPlan(data); ladenListe(); toast.success(s === 'zugewiesen' ? 'Zugewiesen — das Mitglied sieht den Plan in der App' : 'Zuweisung aufgehoben'); }
    catch (e) { toast.error(fehlertext(e)); } finally { setLaeuft(false); }
  };
  const zugewiesen = plan && ['zugewiesen', 'aktiv'].includes(plan.status);
  const fuss = plan && plan.status !== 'abgeloest' && (zugewiesen
    ? <button type="button" className={KNOPF} disabled={laeuft} onClick={() => status('entwurf')}><Undo2 className="h-4 w-4" aria-hidden="true" />Zuweisung aufheben</button>
    : <button type="button" className={`${KNOPF} border-primary bg-primary text-primary-foreground hover:bg-primary/90`} disabled={laeuft} onClick={() => status('zugewiesen')}><Send className="h-4 w-4" aria-hidden="true" />Speichern &amp; in die App</button>);
  return (
    <div className="space-y-[18px]" data-testid="plan-varianten">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[13px] text-muted-foreground">Ernährung · Mealplans</div>
          <h2 className="m-0 text-lg font-bold">Ernährungsplan mit Varianten{plan?.name ? ` · ${plan.name}` : ''}</h2>
          {plan?.varianten && <div className="text-[13px] text-muted-foreground tabular-nums">{datum(plan.start)} – {datum(plan.ende)}{zugewiesen ? ' · in der App' : plan.status === 'entwurf' ? ' · Entwurf' : ''}</div>}
        </div>
        <button type="button" className={KNOPF} aria-expanded={neu} onClick={() => setNeu(!neu)}><Sparkles className="h-4 w-4" aria-hidden="true" />Neuer Plan / aus Vorlage</button>
      </div>
      {liste === null ? <Skeleton className="h-11 w-full" /> : liste.length ? (
        <div role="tablist" aria-label="Pläne mit Varianten" className="flex flex-wrap gap-1.5">
          {liste.map((p) => (
            <button key={p.id} type="button" role="tab" aria-selected={gewaehlt === p.id} onClick={() => setGewaehlt(p.id)} className={chipKlasse(gewaehlt === p.id)}>
              {p.name || `${p.varianten} Varianten`} · {datum(p.start)} – {datum(p.ende)} · {p.status === 'entwurf' ? 'Entwurf' : p.status === 'abgeloest' ? 'abgelöst' : 'zugewiesen'}{p.laeuft ? ' · läuft' : ''}
            </button>
          ))}
        </div>
      ) : <p className="text-sm text-muted-foreground">Noch kein Plan mit Varianten.</p>}
      {neu && <div className={KARTE}><VariantenNeu leadId={leadId} onAbbrechen={() => setNeu(false)} onErzeugt={(data) => { setNeu(false); setPlan(data); setGewaehlt(data.id); ladenListe(); }} /></div>}
      {plan && plan.varianten && <VariantenEditor stil="coach" fuss={fuss} leadId={leadId} plan={plan} onPlan={(p) => { setPlan(p); ladenListe(); }} />}
    </div>
  );
}
